import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  InputBase,
  Paper,
  Popover,
  Badge,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Chip,
} from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import "./Header.css";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { useSelector } from "react-redux";
import { getAllNotifications } from "../../redux/reducers/notificationReducer";
import { useDispatch } from "react-redux";
import ProfileScreen from "../ProfileScreen";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useLocation } from "react-router-dom";
import NotificationPanel from "../../components/notification/Notification";
import GradientButton from "../../components/common/GradiantButton";
import LoginModal from "../../components/common/LoginModal";
import useMediaQuery from "@mui/material/useMediaQuery";
import { scrollToHash } from "../../utils/scrollToHash";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "Properties", path: "/properties" },
  { label: "About Us", path: "/about" },
  { label: "Services", path: "/#services" },
  { label: "Contact", path: "/contact" },
];

const servicesLinkTo = { pathname: "/", hash: "#services" };

const linkTo = (path) => (path === "/#services" ? servicesLinkTo : path);

const isNavActive = (pathname, hash, path) => {
  if (path === "/") return pathname === "/" || pathname === "/home";
  if (path === "/#services") {
    return (pathname === "/" || pathname === "/home") && hash === "#services";
  }
  return pathname === path || pathname.startsWith(`${path}/`);
};

const HeaderBrand = ({ logoSrc }) => (
  <Box
    component={RouterLink}
    to="/"
    className="header-brand"
    sx={{
      display: "flex",
      alignItems: "center",
      gap: { xs: 1, sm: 1.5 },
      textDecoration: "none",
      color: "inherit",
      minWidth: 0,
      flexShrink: 0,
    }}
  >
    <Box
      component="img"
      src={logoSrc}
      alt="E & J Global Realtors"
      sx={{
        height: { xs: 44, sm: 52 },
        width: "auto",
        objectFit: "contain",
        flexShrink: 0,
      }}
    />
    <Box sx={{ minWidth: 0, display: { xs: "none", sm: "block" } }}>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: { sm: "0.85rem", md: "0.95rem" },
          color: "#1a1a2e",
          lineHeight: 1.25,
          letterSpacing: "0.02em",
        }}
      >
        E & J GLOBAL REALTORS
      </Typography>
      <Typography
        sx={{
          fontSize: { sm: "0.55rem", md: "0.62rem" },
          color: "#5c5c6d",
          letterSpacing: "0.04em",
          lineHeight: 1.3,
          display: { xs: "none", md: "block" },
          mt: 0.25,
        }}
      >
        YOUR DREAM | OUR COMMITMENT | A BETTER TOMORROW
      </Typography>
    </Box>
  </Box>
);

const NavLinkItem = ({ link, pathname, hash, accentColor, onNavigate }) => {
  const active = isNavActive(pathname, hash, link.path);
  const sx = {
    position: "relative",
    px: 1.25,
    py: 1,
    fontSize: "0.9rem",
    fontWeight: 500,
    color: "#2d2d3a",
    textDecoration: "none",
    whiteSpace: "nowrap",
    "&::after": {
      content: '""',
      position: "absolute",
      left: "50%",
      bottom: 4,
      transform: active ? "translateX(-50%) scaleX(1)" : "translateX(-50%) scaleX(0)",
      width: "calc(100% - 8px)",
      height: 2,
      borderRadius: 1,
      bgcolor: accentColor || "#FF2D95",
      transition: "transform 0.2s ease",
    },
    "&:hover": { color: accentColor || "#FF2D95" },
  };

  const handleClick = (e) => {
    if (link.path === "/#services") {
      const onHome = pathname === "/" || pathname === "/home";
      if (onHome && hash === "#services") {
        e.preventDefault();
        scrollToHash("#services");
      }
    }
    onNavigate?.();
  };

  return (
    <Box
      component={RouterLink}
      to={linkTo(link.path)}
      className={`header-nav-link${active ? " header-nav-link--active" : ""}`}
      onClick={handleClick}
      sx={sx}
    >
      {link.label}
    </Box>
  );
};

const PostPropertyButton = ({ onClick, compact }) => (
  <Box sx={{ position: "relative", flexShrink: 0 }}>
    <Chip
      label="FREE"
      size="small"
      sx={{
        position: "absolute",
        top: compact ? 3 : -8,
        right: compact ? 3 : -10,
        zIndex: 2,
        fontSize: "0.65rem",
        height: 18,
        fontWeight: 700,
        color: "white",
        background: "linear-gradient(135deg, #22c55e, #16a34a)",
        boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
      }}
    />
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        padding: compact ? "6px 12px" : "6px 14px",
        backgroundColor: "primary.main",
        color: "white",
        borderRadius: "8px",
        fontWeight: 500,
        cursor: "pointer",
        fontSize: compact ? "0.85rem" : "1rem",
        "&:hover": { backgroundColor: "primary.dark" },
      }}
    >
      Post property
    </Box>
  </Box>
);

function Header({ onDrawerToggle, currentPath }) {
  const logourl = useSelector((state) => state.themeValue.logourl);
  const userData = useSelector((state) => state.user);
  const [searchTerm, setSearchTerm] = useState("");
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:768px)");
  const isDesktopNav = useMediaQuery("(min-width:769px)");
  const [guestNavOpen, setGuestNavOpen] = useState(false);

  const userId = useSelector((state) => state.user.userId);
  const isAuthenticated = useSelector((state) => state.user.isAuthenticated);
  const likedItems = useSelector((state) => state.favorites.items || []);
  const theme = useSelector((data) => data.themeValue.colors);
  const themeMode = useSelector((state) => state.themeValue.theme);
  const notifications = useSelector((state) => state.notification.list);
  const unReadNotifications = notifications.filter(
    (notification) => notification.status === "Unread"
  );

  const [openLoginModal, setOpenLoginModal] = useState(false);
  const accentColor = theme?.primary_gradient_start_color || "#FF2D95";
  const logoSrc = logourl?.light_logo || "/Jemins Letter Head.png";

  const handleClick = () => {
    if (!isAuthenticated) {
      setOpenLoginModal(true);
    } else if (isAuthenticated && !userData?.isVerified) {
      alert("Please verify your email to post a property.");
      navigate("/myprofile");
    } else if (isAuthenticated && userData?.isVerified) {
      navigate("/post-your-property");
    }
  };

  useEffect(() => {
    if (userId) {
      dispatch(getAllNotifications(userId));
    }
  }, [userId, dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      setOpenLoginModal(false);
    }
  }, [isAuthenticated]);

  const handleSearch = (e) => {
    if (e.key === "Enter" || e.type === "click") {
      navigate("/search-properties", {
        state: { keyword: searchTerm },
      });
    }
  };

  const handleNotificationClick = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const unreadCount = unReadNotifications.length;
  const notificationOpen = Boolean(notificationAnchor);
  const showSearch = currentPath === "/search-properties";

  const handleMenuClick = () => {
    if (isAuthenticated) {
      onDrawerToggle?.();
    } else {
      setGuestNavOpen(true);
    }
  };

  const closeGuestNav = () => setGuestNavOpen(false);

  const appBarStyle = {
    background: "white",
    borderBottom: "2px solid #bfdbfe",
    boxShadow:
      themeMode === "light"
        ? "0 4px 16px rgba(0, 0, 0, 0.25)"
        : "0 4px 16px rgba(239, 237, 237, 0.1)",
  };

  const searchBar = showSearch ? (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minWidth: 0,
        px: 1,
      }}
    >
      <Paper
        component="div"
        sx={{
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          px: 2,
          py: 0.5,
          width: { sm: "65%", md: "55%" },
          borderRadius: "15px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          border: "1px solid #f0f0f0",
          backgroundColor: "white",
          transition: "all 0.2s ease",
          "&:focus-within": {
            boxShadow: "0 4px 15px rgba(0,0,0,0.12)",
            border: "1px solid #e0e0e0",
          },
        }}
      >
        <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
        <InputBase
          placeholder="Search properties by location, area, or name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch(e)}
          sx={{
            flex: 1,
            fontSize: "0.95rem",
            "& .MuiInputBase-input": { padding: "8px 0" },
          }}
        />
        <GradientButton text="Search" onClick={handleSearch} width={100} height={35} />
      </Paper>
    </Box>
  ) : (
    <Box
      className="header-main-nav"
      sx={{
        flex: 1,
        display: isDesktopNav ? "flex" : "none",
        justifyContent: "center",
        alignItems: "center",
        gap: 0.5,
        minWidth: 0,
      }}
    >
      {NAV_LINKS.map((link) => (
        <NavLinkItem
          key={link.path}
          link={link}
          pathname={location.pathname}
          hash={location.hash}
          accentColor={accentColor}
        />
      ))}
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={3}
        sx={{
          padding: { xs: "0px", sm: "5px" },
          paddingTop: { xs: "4px" },
          paddingBottom: { xs: "4px" },
        }}
        style={appBarStyle}
      >
        <Toolbar
          sx={{
            p: 0,
            px: { xs: 2, sm: 3, md: 4 },
            minHeight: { xs: 64, sm: 72 },
            gap: 1,
            alignItems: "center",
          }}
        >
          <IconButton
            edge="start"
            onClick={handleMenuClick}
            className="menu-button"
            aria-label="Open menu"
            sx={{
              display: isDesktopNav ? "none" : "inline-flex",
              color: "black",
              flexShrink: 0,
            }}
          >
            <MenuIcon />
          </IconButton>

          <HeaderBrand logoSrc={logoSrc} />

          {searchBar}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.5, sm: 1.5 },
              ml: "auto",
              flexShrink: 0,
            }}
          >
            {isAuthenticated && (
              <>
                <Box sx={{ display: isDesktopNav ? "none" : "flex", alignItems: "center" }}>
                  <IconButton onClick={handleNotificationClick} aria-label="Notifications">
                    <Badge badgeContent={unreadCount} color="error">
                      <NotificationsNoneIcon sx={{ color: "black", fontSize: 25 }} />
                    </Badge>
                  </IconButton>
                </Box>
                <Box
                  className="header-icon"
                  sx={{
                    display: isDesktopNav ? "flex" : "none",
                    alignItems: "center",
                  }}
                >
                  <IconButton color="inherit" onClick={handleNotificationClick}>
                    <Badge badgeContent={unreadCount} color="error">
                      <NotificationsNoneIcon sx={{ color: "black", fontSize: 25 }} />
                    </Badge>
                  </IconButton>
                  <IconButton color="inherit" component={Link} to="/favorites" aria-label="Favorites">
                    <FavoriteBorderIcon sx={{ color: "black", fontSize: 25 }} />
                  </IconButton>
                </Box>
                <Popover
                  open={notificationOpen}
                  anchorEl={notificationAnchor}
                  onClose={handleNotificationClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  sx={{
                    "& .MuiPopover-paper": {
                      width: { xs: "100vw", sm: 400 },
                      height: { xs: "100vh", sm: "80vh" },
                      maxHeight: { sm: "80vh" },
                      marginTop: { xs: 0, sm: 1 },
                      marginRight: { xs: 0, sm: 1 },
                      borderRadius: { xs: 0, sm: 2 },
                      boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
                    },
                    "& .MuiBackdrop-root": {
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      backdropFilter: "blur(2px)",
                    },
                  }}
                >
                  <NotificationPanel onClose={handleNotificationClose} isPopover={true} />
                </Popover>
              </>
            )}

            <PostPropertyButton onClick={handleClick} compact={isMobile} />

            {!isMobile && <ProfileScreen />}
          </Box>
        </Toolbar>
      </AppBar>

      {!isAuthenticated && (
        <Drawer
          anchor="left"
          open={guestNavOpen}
          onClose={closeGuestNav}
          PaperProps={{ sx: { width: 280, pt: 2 } }}
        >
          <Typography sx={{ px: 2, pb: 2, fontWeight: 700, color: "#1a1a2e" }}>
            Menu
          </Typography>
          <List>
            {NAV_LINKS.map((link) => (
              <ListItemButton
                key={link.path}
                component={RouterLink}
                to={linkTo(link.path)}
                onClick={(e) => {
                  if (link.path === "/#services") {
                    const onHome =
                      location.pathname === "/" || location.pathname === "/home";
                    if (onHome && location.hash === "#services") {
                      e.preventDefault();
                      scrollToHash("#services");
                    }
                  }
                  closeGuestNav();
                }}
                selected={isNavActive(location.pathname, location.hash, link.path)}
              >
                <ListItemText primary={link.label} />
              </ListItemButton>
            ))}
          </List>
        </Drawer>
      )}

      {openLoginModal && <LoginModal onClose={() => setOpenLoginModal(false)} />}
    </>
  );
}

export default Header;
